/**
 * Converts Google Pay address format to Easons standard address format
 */

export interface GooglePayAddress {
  name?: string;
  postalCode?: string;
  countryCode?: string;
  phoneNumber?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  locality?: string;
  administrativeArea?: string;
  sortingCode?: string;
}

export interface EasonsAddress {
  FirstName: string;
  LastName: string;
  Line1: string;
  Line2?: string;
  City: string;
  CountyCode?: string;
  PostalCode: string;
  CountryCode: string;
  Phone?: string;
}

/**
 * Converts a Google Pay billing address to Easons address format
 * @param googlePayAddress The address from Google Pay response
 * @returns The address in Easons format
 */
export function convertGooglePayAddress(googlePayAddress: GooglePayAddress): EasonsAddress {
  // Parse the name into first and last name
  const fullName = googlePayAddress.name || '';
  const nameParts = fullName.trim().split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Build the Easons address
  const easonsAddress: EasonsAddress = {
    FirstName: firstName,
    LastName: lastName,
    Line1: googlePayAddress.address1 || '',
    Line2: googlePayAddress.address2 || googlePayAddress.address3 || '',
    City: googlePayAddress.locality || '',
    CountyCode: googlePayAddress.administrativeArea || '',
    PostalCode: googlePayAddress.postalCode || '',
    CountryCode: googlePayAddress.countryCode || '',
    Phone: googlePayAddress.phoneNumber || ''
  };

  // Remove undefined optional fields
  if (!easonsAddress.Line2) {
    delete easonsAddress.Line2;
  }
  if (!easonsAddress.CountyCode) {
    delete easonsAddress.CountyCode;
  }
  if (!easonsAddress.Phone) {
    delete easonsAddress.Phone;
  }

  return easonsAddress;
}

/**
 * Converts Google Pay billing address with strict fallbacks for SagePay/Opayo compatibility
 * Ensures all required fields are populated to prevent payment gateway failures
 * @param googlePayAddress The address from Google Pay response
 * @returns The address in Easons format with guaranteed required fields
 */
export function convertGooglePayBillingAddress(googlePayAddress: GooglePayAddress): EasonsAddress {
  // Parse the name into first and last name with fallbacks
  const fullName = googlePayAddress.name || '';
  const nameParts = fullName.trim().split(' ');
  const firstName = nameParts[0] || 'Customer'; // Fallback if no name
  const lastName = nameParts.slice(1).join(' ') || 'Surname'; // Fallback if no surname

  // Build the Easons address with guaranteed required fields
  const easonsAddress: EasonsAddress = {
    FirstName: firstName,
    LastName: lastName,
    Line1: googlePayAddress.address1 || 'Address Line 1', // Fallback if missing
    Line2: googlePayAddress.address2 || googlePayAddress.address3 || '',
    City: googlePayAddress.locality || googlePayAddress.administrativeArea || 'City', // Fallback if missing
    CountyCode: googlePayAddress.administrativeArea || '',
    PostalCode: googlePayAddress.postalCode || googlePayAddress.sortingCode || '00000', // Fallback if missing
    CountryCode: (googlePayAddress.countryCode || 'IE').toUpperCase(), // Fallback to Ireland
    Phone: googlePayAddress.phoneNumber || ''
  };

  // Remove undefined optional fields (but keep empty strings for required fields)
  if (!easonsAddress.Line2) {
    delete easonsAddress.Line2;
  }
  if (!easonsAddress.CountyCode) {
    delete easonsAddress.CountyCode;
  }
  if (!easonsAddress.Phone) {
    delete easonsAddress.Phone;
  }

  return easonsAddress;
}

export function requiredForCountry(CountryCode: string) {
  const countriesRequiringCounty = ['IE', 'GB', 'US', 'CA'];
  return countriesRequiringCounty.includes(CountryCode?.toUpperCase());
}